'use client'

/**
 * Marketing-page sample of the PulseSignal dashboard.
 *
 * Mirrors the new watchlist-centric Overview (Phase 4 direction): no more
 * "modules active / data points / coverage bars" engineering instrumentation.
 * What a Pro user actually opens on day one, their watchlist's weekly KPIs,
 * the severity-coded changes feed, and a sampling of module-shortcut tiles
 * (a marketing-mock subset of the ten signal modules the
 * homepage tells the story of).
 *
 * Keep in lock-step with app/dashboard/page.tsx when that layout ships.
 */

import {
  AlertTriangle,
  AlertCircle,
  Info,
  Sparkles,
  TrendingUp,
  Bell,
  Activity,
  Clock,
  DollarSign,
  Coins,
  Users,
  Briefcase,
  MessageSquare,
  Rocket,
  Layers,
  Compass,
} from 'lucide-react'
import {
  signalAgoLabel,
  signalModuleLabel,
  useSampleSignals,
} from '@/lib/sampleSignals'

type Severity = 'critical' | 'notable' | 'info'

// Three watchlist-scoped KPIs that replace the old "modules active /
// data points / narratives" trio. These are what a customer cares about
// the moment they open the app on Monday.
const KPIS: { label: string; value: string; tag?: string; icon: any; iconColor: string }[] = [
  { label: 'New signals this week', value: '47',  icon: TrendingUp, iconColor: 'text-terra-600' },
  { label: 'Critical alerts unread', value: '4', tag: 'page-worthy', icon: Bell, iconColor: 'text-maroon-600' },
  { label: 'Companies moving', value: '12', tag: 'of 60 tracked', icon: Activity, iconColor: 'text-olive-700' },
]

type ActivityRow = { id: number; severity: Severity; company: string; module: string; text: string; ago: string }

// FALLBACK ONLY (LG-26): static illustrative activity rows rendered ONLY
// when GET /api/v1/public/sample-signals fails or returns empty, so the
// mock dashboard's feed never goes blank. Live recent signals replace
// these rows when available. Same list pattern as the real dashboard.
//
// Company names here are FICTIONAL on purpose. Attaching specific,
// fact-checkable pricing / funding / leadership claims to real companies
// reads as us getting their facts wrong, even behind the "sample" badge.
// Invented but plausible SaaS names keep the mock realistic with no false
// claim pinned on a real business.
// Four DISTINCT modules across a severity mix so even this short feed shows
// breadth (not two pricing rows). Fictional companies, never a real claim.
const FALLBACK_ACTIVITY: ActivityRow[] = [
  {
    id: 1,
    severity: 'critical',
    company: 'Acme Cloud',
    module: 'PRICING',
    text: 'Business tier repriced. Per-seat up, annual prepay discount widened to 20%.',
    ago: '14m',
  },
  {
    id: 2,
    severity: 'notable',
    company: 'Vertex Labs',
    module: 'FUNDING',
    text: 'Series B round announced. New growth-stage lead signals an enterprise GTM push.',
    ago: '1h',
  },
  {
    id: 3,
    severity: 'info',
    company: 'Beacon',
    module: 'TECH STACK',
    text: 'Migrated to a managed lakehouse. Dropped a legacy analytics SDK.',
    ago: '3h',
  },
  {
    id: 4,
    severity: 'info',
    company: 'Tessera',
    module: 'HIRING',
    text: '8 new enterprise AE roles posted across three regions.',
    ago: '5h',
  },
]

// Key module shortcuts replace the old coverage bar chart. Each tile is
// a one-click filter into that module scoped to the user's watchlist.
// The mock surfaces a representative slice. The full ten-module
// story lives on the homepage.
const SHORTCUTS: { module: string; icon: any; iconColor: string; weekly: number }[] = [
  { module: 'Pricing',          icon: DollarSign,    iconColor: 'text-terra-600', weekly: 12 },
  { module: 'Funding',          icon: Coins,         iconColor: 'text-olive-700', weekly: 3 },
  { module: 'Hiring',           icon: Users,         iconColor: 'text-petrol-700', weekly: 21 },
  { module: 'Leadership',       icon: Briefcase,     iconColor: 'text-terra-600', weekly: 4 },
  { module: 'Sentiment',        icon: MessageSquare, iconColor: 'text-olive-700', weekly: 7 },
  { module: 'Launches',         icon: Rocket,        iconColor: 'text-petrol-700', weekly: 5 },
  { module: 'Tech Stack',       icon: Layers,        iconColor: 'text-olive-700', weekly: 6 },
  { module: 'Positioning',      icon: Compass,       iconColor: 'text-terra-600', weekly: 2 },
]

const SEVERITY_ICON: Record<Severity, any> = {
  critical: AlertTriangle,
  notable: AlertCircle,
  info: Info,
}
const SEVERITY_BORDER: Record<Severity, string> = {
  critical: 'border-l-maroon-500',
  notable:  'border-l-terra-500',
  info:     'border-l-petrol-500',
}
const SEVERITY_BADGE: Record<Severity, string> = {
  critical: 'bg-maroon-500/10 text-maroon-600 border border-maroon-500/30',
  notable:  'bg-terra-500/10 text-terra-700 border border-terra-500/30',
  info:     'bg-petrol-500/10 text-petrol-700 border border-petrol-500/30',
}

export default function MockDashboard() {
  // LG-26: live recent signals are the PRIMARY feed content; only the
  // signal/event rows swap to live data. The KPI tiles, greeting, and
  // module shortcuts stay as the illustrative product mock.
  const live = useSampleSignals()
  const activity: ActivityRow[] = live
    ? live.slice(0, 4).map((s, i) => ({
        id: i + 1,
        severity: s.severity,
        company: s.company_name,
        module: signalModuleLabel(s.event_type),
        text: s.summary,
        ago: signalAgoLabel(s.occurred_at),
      }))
    : FALLBACK_ACTIVITY

  return (
    <div className="rounded-2xl border border-ink-900/10 bg-cream-100 overflow-hidden shadow-[0_24px_60px_-24px_rgba(0,0,0,0.18)]">
      {/* Window chrome */}
      <div className="flex items-center gap-1.5 px-4 sm:px-5 h-9 border-b border-ink-900/10 bg-cream-50">
        <span className="w-2.5 h-2.5 rounded-full bg-ink-900/15" />
        <span className="w-2.5 h-2.5 rounded-full bg-ink-900/15" />
        <span className="w-2.5 h-2.5 rounded-full bg-ink-900/15" />
        <span className="ml-3 text-[12px] uppercase tracking-widest text-ink-600 font-mono">
          app.pulsesignal.co/dashboard
        </span>
        {/* Honest badge: "live" only while real signals from the public
            endpoint are rendered. The static fallback rows are clearly
            labelled "sample" instead (never-blank stays intact). */}
        {live ? (
          <span className="ml-auto text-[12px] uppercase tracking-widest text-olive-600 font-mono">
            ● live
          </span>
        ) : (
          <span className="ml-auto text-[12px] uppercase tracking-widest text-ink-600 font-mono">
            ● sample
          </span>
        )}
      </div>

      {/* Body */}
      <div className="p-4 sm:p-5 space-y-5">
        {/* Greeting + last refreshed strip */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[15px] font-semibold text-ink-900">Welcome back, Maya</div>
            <div className="flex items-center gap-1.5 text-[12px] text-ink-600 mt-1">
              <Clock className="w-3 h-3" />
              <span>Updated 02:00 UTC today · next cycle in 19h</span>
            </div>
          </div>
          <span className="text-[12px] uppercase tracking-widest text-ink-600">48 / 60 tracked</span>
        </div>

        {/* This-week KPI tiles, watchlist-scoped, not engineering metrics */}
        <div className="grid grid-cols-3 gap-2 sm:gap-3">
          {KPIS.map((k) => {
            const Icon = k.icon
            return (
              <div key={k.label} className="rounded-xl border border-ink-900/10 bg-cream-50 p-3 sm:p-3.5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[12px] font-medium text-ink-600 uppercase tracking-wider leading-tight">{k.label}</span>
                  <Icon className={`w-3.5 h-3.5 ${k.iconColor} shrink-0`} />
                </div>
                <div className="flex items-baseline gap-1.5">
                  <span className="text-2xl sm:text-3xl font-semibold text-ink-900 tabular-nums leading-none">{k.value}</span>
                  {k.tag && <span className="text-[12px] text-ink-600 font-medium">{k.tag}</span>}
                </div>
              </div>
            )
          })}
        </div>

        {/* Activity feed */}
        <div>
          <div className="flex items-center gap-1.5 mb-2.5">
            <Sparkles className="w-3 h-3 text-terra-600" />
            <span className="text-[12px] font-semibold uppercase tracking-widest text-ink-600">This week on your watchlist</span>
          </div>
          <div className="space-y-1.5" data-signals-source={live ? 'live' : 'fallback'}>
            {activity.map((n) => {
              const Icon = SEVERITY_ICON[n.severity]
              return (
                <div
                  key={n.id}
                  className={`rounded-lg border border-ink-900/10 bg-cream-50 p-3 border-l-4 ${SEVERITY_BORDER[n.severity]}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                        <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[12px] font-medium uppercase tracking-wider ${SEVERITY_BADGE[n.severity]}`}>
                          <Icon className="w-2.5 h-2.5" />
                          {n.severity}
                        </span>
                        <span className="text-[12px] text-ink-700 font-medium">{n.company}</span>
                        <span className="text-[12px] text-ink-600 uppercase tracking-wider truncate">{n.module}</span>
                      </div>
                      <p className="text-[13px] text-ink-900 leading-relaxed">{n.text}</p>
                    </div>
                    <span className="text-[12px] text-ink-600 whitespace-nowrap shrink-0">{n.ago}</span>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Key module shortcuts replace the old coverage bars */}
        <div>
          <div className="text-[12px] font-semibold uppercase tracking-widest text-ink-600 mb-2.5">
            Jump into a module
          </div>
          <div className="grid grid-cols-4 gap-2">
            {SHORTCUTS.map((s) => {
              const Icon = s.icon
              return (
                <div key={s.module} className="rounded-xl border border-ink-900/10 bg-cream-50 px-2.5 py-2.5 flex items-center gap-2">
                  <div className="w-6 h-6 rounded bg-cream-100 flex items-center justify-center shrink-0">
                    <Icon className={`w-3 h-3 ${s.iconColor}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] font-medium text-ink-800 truncate">{s.module}</div>
                    <div className="text-[12px] text-ink-600 tabular-nums">{s.weekly} this week</div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
